import { useCallback, useMemo, useState } from 'react'

export type SortDirection = 'asc' | 'desc'
export type SortState = 'none' | SortDirection

export const SORT_STATES: SortState[] = ['none', 'desc', 'asc']

export interface SortConfig {
  field: string
  direction: SortDirection
}

export interface UseSortingOptions {
  defaultField?: string
  defaultDirection?: SortDirection
  initialState?: 'none' | 'default'
}

export interface UseSortingReturn {
  sortField: string
  sortDirection: SortDirection
  sortString: string
  handleSort: (field: string, direction: SortState) => void
  handleHeadCellClick: (field: string) => void
  isFieldSorted: (field: string) => boolean
  isFieldSortedAsc: (field: string) => boolean
  isFieldSortedDesc: (field: string) => boolean
}

/**
 * Custom hook for managing table sorting with a 3-state cycle (none -> desc -> asc -> none)
 *
 * @param options - Configuration options
 * @param options.defaultField - Default sort field (used when state is 'none')
 * @param options.defaultDirection - Default sort direction (default: 'desc')
 * @param options.initialState - Initial state: 'none' (no arrows active) or 'default' (use default field/direction)
 *
 * @returns Sorting state and handlers
 *
 * @example
 * const { sortField, sortDirection, sortString, handleHeadCellClick } = useSorting({
 *   defaultField: 'marketCap',
 *   defaultDirection: 'desc',
 *   initialState: 'none'
 * })
 *
 * // In API call
 * useCryptoMarketDataQry({ sort: sortString })
 *
 * // In table header
 * <th onClick={() => handleHeadCellClick('price')}>Price</th>
 */
export const useSorting = ({
  defaultField = '',
  defaultDirection = 'desc',
  initialState = 'none'
}: UseSortingOptions = {}): UseSortingReturn => {
  // Combine field and direction into a single state object
  // Empty field means 'none' state (no arrows active)
  const [sort, setSort] = useState<SortConfig>({
    field: initialState === 'none' ? '' : defaultField,
    direction: defaultDirection
  })

  // Build sort string for API
  // If field is empty (none state), use default sorting
  const sortString = useMemo(
    () => (sort.field ? `${sort.field}-${sort.direction}` : `${defaultField}-${defaultDirection}`),
    [sort.field, sort.direction, defaultField, defaultDirection]
  )

  // Handle sort change
  const handleSort = useCallback(
    (field: string, direction: SortState) => {
      if (direction === 'none') {
        // Set to empty field to indicate 'none' state (no arrows active)
        setSort({ field: '', direction: defaultDirection })
      } else {
        setSort({ field, direction })
      }
    },
    [defaultDirection]
  )

  // Handle clicking on table head to cycle through sort states
  const handleHeadCellClick = useCallback(
    (field: string) => {
      const currentState: SortState = sort.field === field ? sort.direction : 'none'
      const currentIndex = SORT_STATES.indexOf(currentState)
      const nextIndex = (currentIndex + 1) % SORT_STATES.length
      const nextState = SORT_STATES[nextIndex]

      handleSort(field, nextState)
    },
    [sort.field, sort.direction, handleSort]
  )

  // Helper functions to check sort state
  const isFieldSorted = useCallback((field: string) => sort.field === field, [sort.field])

  const isFieldSortedAsc = useCallback(
    (field: string) => sort.field === field && sort.direction === 'asc',
    [sort.field, sort.direction]
  )

  const isFieldSortedDesc = useCallback(
    (field: string) => sort.field === field && sort.direction === 'desc',
    [sort.field, sort.direction]
  )

  return {
    sortField: sort.field,
    sortDirection: sort.direction,
    sortString,
    handleSort,
    handleHeadCellClick,
    isFieldSorted,
    isFieldSortedAsc,
    isFieldSortedDesc
  }
}
